import { identity, prop } from 'ramda';
import { get } from 'redux/shelf';
import { createSelector } from 'reselect';

/**
 * List of merchants
 */
export const getMerchants = createSelector(
  get('user', 'data', 'merchants'),
  identity
);

/**
 * Selected merchant
 */
export const getMerchant = createSelector(get('user', 'merchant'), identity);

/**
 * First merchant in list, who has ID
 */
export const getFirstMerchant = createSelector(
  getMerchants,
  (merchants: any[]) =>
    merchants.find(({ merchantID }) => !!merchantID) || merchants[0]
);

export const getMerchantById = (id) =>
  createSelector(getMerchants, (merchants: any[]) =>
    merchants.find(({ merchantID }) => merchantID === id)
  );

/**
 * ID of first merchant in list
 */
export const getFirstMerchantId = createSelector(
  getFirstMerchant,
  prop('merchantID')
);

/**
 * First key in api keys of selected merchant
 */
export const getMerchantApi = createSelector(
  getMerchant,
  (merchant) => merchant?.apiKeys[0]
);

export const getMerchantApiProp = (value) =>
  createSelector(getMerchantApi, prop(value));

export const getMerchantApiKey = createSelector(getMerchantApi, prop('apiKey'));

/**
 * [Creator] Selected merchant prop
 */
export const getMerchantProp = (value) =>
  createSelector(getMerchant, prop(value));

export const getShowShopifyIntegration = createSelector(
  getMerchantProp('isPrivateShopify'),
  getMerchantProp('platform'),
  (isPrivateShopify, platform) => platform === 'shopify' && !isPrivateShopify
);

export const getFeatures = createSelector(
  getMerchantProp('features'),
  (features: any) => features
);

/**
 * [Creator] Selected merchant features
 */
export const getFeaturesProp = (feature: string) =>
  createSelector(getFeatures, prop(feature));

export const isShopifyBillingMerchant = createSelector(
  getMerchantProp('platform'),
  getMerchantProp('shopifyCap'),
  getMerchantProp('shopifyRecurring'),
  (platform, shopifyCap, shopifyRecurring) =>
    (platform === 'shopify' && shopifyCap) || shopifyRecurring
);

export const hasV3MJS = createSelector(
  getMerchantProp('mjsVersion'),
  (mjsVersion: string) => (mjsVersion ? mjsVersion.slice(0, 2) === 'v3' : false)
);

/**
 * [Creator] Selected merchant feature
 */

export const getFeature = (feature: string) =>
  createSelector(
    getFeaturesProp('features'),
    (f) =>
      f?.[feature] &&
      (['api', 'products'].includes(feature) || f?.[feature]?.enabled) &&
      f?.[feature]
  );

export const getAvailableFeatures = createSelector(
  getFeaturesProp('features'),
  getMerchantProp('hasPersonalizedSearch'),
  getMerchantProp('hasPersonalizedRecommendations'),
  getMerchantProp('useLucy'),
  (features, personalizedSearch, personalizedRecommendations, useLucy) =>
    features && {
      personalizedSearch,
      personalizedRecommendations,
      useLucy: useLucy || personalizedSearch,
      ...Object.keys(features)
        .filter((name) => features[name].enabled)
        .reduce((acc, name) => ({ ...acc, [name]: features[name] }), {}),
    }
);

/**
 * [Creator] Check if merchant has feature
 */
export const hasFeature = (feature: string) =>
  createSelector(getAvailableFeatures, (features) => !!features[feature]);

export const hasRules = createSelector(
  getMerchantProp('plan'),
  (plan) => plan !== 'small'
);

export const getPlanPriceValue = createSelector(getFeatures, prop('price'));

export const getAllowedManualSynonymsCount = createSelector(
  getFeature('synonym'),
  (f) => (!f ? 0 : f.count)
);

export const areUnlimitedSynonymsAvailable = createSelector(
  getFeature('synonym'),
  (f) => (!f ? false : f.unlimited)
);

export const areDiscountStickersAvailable = createSelector(
  getFeature('sticker'),
  (f) => (!f ? false : f.type && f.type.discount)
);

export const areFreeShippingStickersAvailable = createSelector(
  getFeature('sticker'),
  (f) => (!f ? false : f.type && f.type['free-shipping'])
);

export const getAvailableStickers = createSelector(getFeature('sticker'), (f) =>
  !f ? {} : Object.keys(f.type)
);

export const getAllowedPinnedQueriesCount = createSelector(
  getFeature('pin'),
  (f) => (!f || !f.queries ? 0 : f.queries.count)
);

export const getAllowedPinnedProductsCount = createSelector(
  getFeature('pin'),
  (f) => (!f || !f.products ? 5 : f.products.count)
);

export const areUnlimitedPinnedQueriesAvailable = createSelector(
  getFeature('pin'),
  (f) => (!f || !f.queries ? false : f.queries.unlimited)
);

export const areUnlimitedPinnedProductsAvailable = createSelector(
  getFeature('pin'),
  (f) => (!f || !f.products ? false : f.products.unlimited)
);

export const getAnalyticsRetentionDays = createSelector(
  getMerchantProp('role'),
  getFeature('analytics'),
  (role, analytics) =>
    role === 'admin' || role === 'partner'
      ? null
      : !analytics.unlimited && analytics.retentionDays
);

export const hasSuggestedSynonymsFeature = createSelector(
  getFeature('synonym'),
  (f) => f && f.recommend
);

export const collectionsAutoSyncAvailable = getMerchantProp(
  'collectionAutoSyncAvailable'
);

export const collectionsManualSyncAvailable = createSelector(
  collectionsAutoSyncAvailable,
  getMerchantProp('platform'),
  (autoSyncAvailable, platform) =>
    platform === 'shopify' ? !autoSyncAvailable : autoSyncAvailable
);

export const mjsWithLayouts = createSelector(
  getMerchantProp('mjsVersion'),
  (mjsVersion: string) => {
    const [major, minor] = mjsVersion.split('.').map((v) => +v);
    return major >= 7 && minor >= 1;
  }
);

export const productWebhooksAvailable = getMerchantProp(
  'productWebhooksAvailable'
);
